'use client';

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Select, Option } from 'src/components';
import ControlLabel from './ControlLabel';

interface ControlledSelectProps {
  name: string;
  options: { value: string | number; label: string }[];
  label?: string;
  rules?: Record<string, unknown>;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

const ControlledSelect = ({ ...props }: ControlledSelectProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  if (!props) return null;

  const errorMessage = props?.helperText || 'This field is required';
  const required = props?.required || !!props.rules?.required;
  const testId = props['data-testid'] || `select-${props.name}`;
  const errorId = `${props.name}-error`;
  const hasError = !!errors[props.name];

  if (!props.options) return null;

  return (
    <Controller
      name={props.name}
      control={control}
      rules={{
        required: props.required ? errorMessage : false,
        ...props.rules,
      }}
      render={({ field }) => {
        return (
          <ControlLabel
            label={props?.label || ''}
            required={required}
            hasTooltip={props.hasTooltip}
            tooltipText={props.tooltipText}
          >
            <div data-testid={testId}>
              <Select
                id={props.name}
                value={field.value || ''}
                placeholder={props?.placeholder || 'Select...'}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                error={hasError}
                size={props.size || 'md'}
                disabled={props.disabled}
                className={`rounded-full ${props.className}`}
                aria-label={
                  props.label || props.placeholder || 'Select an option'
                }
                aria-invalid={hasError}
                aria-describedby={hasError ? errorId : undefined}
                aria-required={required}
              >
                {props.options?.length === 0 ? (
                  <Option value="" disabled>
                    No options available
                  </Option>
                ) : (
                  props.options?.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))
                )}
              </Select>
            </div>
            {hasError && (
              <p
                id={errorId}
                role="alert"
                className="text-red-500 text-xs mt-1"
                data-testid={`${testId}-error`}
              >
                {errors[props.name]?.message as string}
              </p>
            )}
          </ControlLabel>
        );
      }}
    />
  );
};

export default ControlledSelect;
